import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import './Custom-Upload.scss';
import { useDropzone } from 'react-dropzone';
import { Attachment, Error } from '@material-ui/icons';
import { FormControlLabel, LinearProgress, Radio, RadioGroup, TextField } from '@material-ui/core';
import { getPreSignedUrl } from '../../../utils/PropertyAPIUtils';
import {
  abortMultipartUpload,
  bytesToSize,
  calculateFileChecksum,
  calculateTotalChunks,
  CHUNK_SIZE,
  completeMultipartUpload,
  convertFileToBase64,
  sanitizeFileName,
} from '../../../utils/commonUtils';
import { scanPdfSecurity } from '../../../utils/pdfSecurityUtils';
import { RemoveCircleRounded, PictureAsPdfOutlined } from '@material-ui/icons';
import CancelIcon from '@mui/icons-material/Cancel';
import { handleValidation } from '../../../utils/formUtils';
import { isEmpty } from 'lodash';
import { notificationManager } from '../../../utils/NotificationUtils';
import { ENVIRONMENT, PROPERTY_SERVICE_URL } from '../../../env/Configurations';
import { MAX_PDF_SIZE, MENU_CONFIG_MAX_PDF_SIZE } from '../../../utils/Constants';

const baseStyle = {
  flex: 1,
  display: 'flex',
  alignItems: 'stretch',
  justifyContent: 'center',
  borderWidth: 1,
  borderRadius: 10,
  borderColor: '#CCCCCC',
  borderStyle: 'dashed',
  backgroundColor: '#fff',
  color: '#CCCCCC',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  minHeight: 95,
};

const activeStyle = {
  borderColor: '#03E67C',
};

const acceptStyle = {
  borderColor: '#03E67C',
};

const rejectStyle = {
  borderColor: '#ff1744',
};

const CustomUpload = (props) => {
  const {
    item,
    name,
    additionalInput,
    onUpload,
    webUrlPlaceholder,
    radioDisable,
    disableName,
    setDeletePopup,
    isDocUpload,
    setFormData,
    isMenuImages = [],
    isCustomMenu = false,
    showNotification = false,
    hotelId,
    maxFileSize,
  } = props;

  const [document, setDocument] = useState({
    url: item?.value?.url,
    type: item?.value?.type,
    name: item?.value?.name,
  });

  const [documentType, setDocumentType] = useState(
    item && item.value?.type ? item?.value?.type : 'S3',
  );
  const [progress, setProgress] = useState(0);
  const [acceptedFile, setAcceptedFile] = useState(null);
  const [uploadError, setUploadError] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [validations, setValidations] = useState(item.validations);
  const [upload, setUpload] = useState(false);
  const abortControllerRef = useRef(null); // Add AbortController ref

  useEffect(() => {
    const validationsList = [...item.validations, 'htmlError'];
    setValidations(validationsList);
  }, [item.validations]);

  useEffect(() => {
    const currField = { ...item };
    currField.value = {
      url: document.url,
      type: document.type,
      name: document.name,
    };
    if (document.name && setFormData && !isCustomMenu) {
      const currField2 = { ...name };
      currField2.value = document.name.split('.')[0];
      setFormData({ name: currField2 });
    }
    handleValidation(currField);
    onUpload(currField);
  }, [document]);

  const onDropAccepted = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles[0].size > maxFileSize) {
        if (maxFileSize === MAX_PDF_SIZE) {
          notificationManager('danger', `Cannot upload files larger than 10 MB.`);
          return;
        } else if (maxFileSize === MENU_CONFIG_MAX_PDF_SIZE) {
          notificationManager('danger', `Cannot upload files larger than 50 MB.`);
          return;
        }
      }
      if (!isEmpty(isMenuImages)) {
        return setImageError(true);
      } else if (isEmpty(isMenuImages) && acceptedFiles && acceptedFiles.length > 0) {
        handleDroppredFile(acceptedFiles[0]);
      }
    },
    [isMenuImages],
  );

  const onDropRejected = useCallback((rejectedFiles) => {
    if (rejectedFiles?.length > 0) {
      setAcceptedFile(rejectedFiles[0]);
      setUploadError(true);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, open } =
    useDropzone({
      accept: 'application/pdf',
      multiple: false,
      noClick: true,
      onDropAccepted,
      onDropRejected,
    });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept],
  );

  useDropzone({ accept: 'application/pdf', multiple: false, onDropAccepted });

  const handleDroppredFile = async (fileObj) => {
    setAcceptedFile(fileObj);
    setProgress(0);
    setUpload(true);

    if (abortControllerRef?.current) {
      abortControllerRef?.current?.abort();
    }
    abortControllerRef.current = new AbortController();


    try {
      await scanPdfSecurity(fileObj);
    } catch (securityError) {
      setUpload(false);
      setUploadError(true);
      notificationManager('danger', securityError?.message || 'Upload blocked: Unsafe PDF content detected.');
      return;
    }

    const fileSize = fileObj?.size;
    if (fileSize < 2 * 1024 * 1024) {
      convertFileToBase64(fileObj)
        .then(async (base64) => {
          const input = {
            ...additionalInput,
            contentLength: fileObj.size,
            contentType: fileObj.type,
            filename: sanitizeFileName(fileObj.name),
            skipContentValidation: false,
            isDocUpload: isDocUpload ?? false,
            contents: base64,
          };
          try {
            const res = await getPreSignedUrl(input, {
              signal: abortControllerRef?.current?.signal,
            });
            setProgress(50);

            if (res?.data?.getPreSignUrlCMS?.key) {
              setProgress(100);
              setDocument({
                url: res?.data?.getPreSignUrlCMS?.key,
                type: 'S3',
                name: sanitizeFileName(fileObj.name),
              });
              setAcceptedFile(null);
              setUpload(false);
            } else {
              setUpload(false);
              setUploadError(true);
              setImageError(false);
            }
          } catch (exception) {
            setUpload(false);
            setUploadError(true);
            setImageError(false);

            if (ENVIRONMENT === 'dev') {
              console.log(exception?.message);
            }

            if (exception?.message) {
              notificationManager('danger', exception?.message);
            } else {
              notificationManager('danger', 'Error while uploading file.');
            }
          }
        })
        .catch((error) => {
          setUpload(false);
          if (ENVIRONMENT === 'dev') {
            console.log(error?.message, 'error');
          }

          notificationManager('danger', 'Error converting file to base64');
        });
    } else {
      const totalChunks = calculateTotalChunks(fileSize);
      let uploadId = null;
      const fileChecksum = await calculateFileChecksum(fileObj);

      try {
        const startUploadResponse = await fetch(
          `${PROPERTY_SERVICE_URL}/startMultipartUpload`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              hotelId,
              groupId: additionalInput?.groupId,
              type: additionalInput?.type,
              propertyType: additionalInput?.propertyType,
              contentType: fileObj?.type,
              filename: sanitizeFileName(fileObj?.name),
              skipContentValidation: false,
              IsCMSRequest: true,
              fileSize,
              fileChecksum,
            }),
            signal: abortControllerRef?.current?.signal,
          },
        );

        if (!startUploadResponse.ok) {
          throw new Error(`Failed to start upload: ${startUploadResponse.status}`);
        }

        const chunkData = await startUploadResponse.json();
        uploadId = chunkData?.data?.key;

        try {
          for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
            const start = chunkIndex * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, fileSize);
            const fileChunk = fileObj?.slice(start, end);

            const base64Chunk = await convertFileToBase64(fileChunk);

            const response = await fetch(`${PROPERTY_SERVICE_URL}/uploadMultipartChunk`, {
              method: 'POST',
              headers: {
                Referer: 'http://localhost:3000/',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                groupId: additionalInput?.groupId,
                hotelId,
                uploadId,
                sequenceNumber: chunkIndex + 1,
                contents: base64Chunk,
              }),
              signal: abortControllerRef?.current?.signal,
            });

            if (!response.ok) {
              throw new Error(`Failed to upload chunk ${chunkIndex + 1}: ${response.status}`);
            }
            const currentProgress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
            setProgress(currentProgress);
          }

          const completeResponse = await completeMultipartUpload(
            additionalInput?.groupId,
            hotelId,
            uploadId,
          );

          if (completeResponse?.status === 'ok' && completeResponse?.data?.status === true) {
            const imageUrl = completeResponse?.data?.message;
            if (imageUrl) {
              setProgress(100);
              setDocument({
                url: completeResponse?.data?.message,
                type: 'S3',
                name: sanitizeFileName(fileObj?.name),
              });
              setUpload(false);
              setUploadError(false);
            }
          } else {
            setUpload(false);
            console.error('File upload failed:', completeResponse);
            throw new Error('Failed to complete upload');
          }
        } catch (error) {
          setUpload(false);
          console.error('Error during file upload:', error);
          setUploadError(true);
          if (uploadId) {
            await abortMultipartUpload(uploadId, additionalInput?.groupId, hotelId);
          }
        }
      } catch (error) {
        setUpload(false);
        console.error('Error in multipart upload:', error);
        setUploadError(true);
      }
    }
  };

  const onDocumentTypeChange = (e) => {
    setDocumentType(e.target.value);
    setDocument({ url: '', type: e.target.value });
  };

  const onDocDelete = () => {
    if (abortControllerRef?.current) {
      abortControllerRef?.current?.abort();
    }
    setDocument({ url: '', type: '' });
    setAcceptedFile(null);
    setDocumentType('S3');
    setUploadError(false);
    setImageError(false);
    setUpload(false);
  };

  const handleDeleteDoc = () => {
    setImageError(false);
    if (setDeletePopup) {
      setDeletePopup(true);
    } else {
      onDocDelete();
    }
  };

  const onChange = (e) => {
    try {
      const inputValue = e.target.value;
      const currField = { ...item };
      if (typeof currField.value !== 'object' || currField.value === null) {
        currField.value = {};
      }
      currField.value.type = 'WEB_URL';
      currField.value.url = inputValue;
      currField.validations = validations;
      handleValidation(currField);
      onUpload(currField);
    } catch (error) {
      notificationManager('danger', 'Error when getting on change input.');
    }
  };

  const getErrorMessage = () => {
    const errorMsgs = [];
    validations &&
      validations.forEach((validation) => {
        if (item.errors[validation]) {
          errorMsgs.push(item.errors[validation]);
        }
      });
    return errorMsgs.length > 0 ? errorMsgs[0] : null;
  };

  return (
    <div className='custom-upload mb-5'>
      {showNotification && !item.value.url && !document.url && (
        <div className='Undefine-error'>Upload a PDF or paste a URL.</div>
      )}
      {((document?.type === 'S3' && document?.url && !isCustomMenu) ||
        (item?.value?.type === 'S3' && item?.value?.url)) &&
      !imageError ? (
        <div className='row'>
          <div className='col-md-12'>
            <div className='attached-doc d-flex justify-content-between p-3 align-items-center'>
              <div>
                {!disableName && <div className='attached-doc-name mb-2'>{item?.fieldName}</div>}
                <div>
                  <PictureAsPdfOutlined className='me-2' />
                  {item?.value?.name}
                </div>
              </div>
              <CancelIcon
                className=' pointer me-1 '
                onClick={(e) => handleDeleteDoc()}
              ></CancelIcon>
            </div>
          </div>
        </div>
      ) : (
        <div className='row'>
          {!radioDisable && (
            <div className='col-md-12'>
              <RadioGroup
                row
                aria-label='documenttype'
                onChange={onDocumentTypeChange}
                value={documentType}
                className='document-type mb-3'
                name='row-radio-buttons-group'
              >
                <FormControlLabel
                  value='S3'
                  control={<Radio color='primary' />}
                  label='Upload PDF Document'
                  className='me-5'
                />
                <FormControlLabel
                  value='WEB_URL'
                  control={<Radio color='primary' />}
                  label='Paste URL'
                />
              </RadioGroup>
            </div>
          )}
          {documentType === 'S3' && (
            <div className='col-md-12 img-drop-zone'>
              {upload ? (
                <div className='d-flex justify-content-center align-items-center progress-bar-screen'>
                  <div className='upload-progress p-3 d-flex justify-content-around'>
                    <div>{acceptedFile?.name}</div>
                    <div className='upload-progress-title'>Uploading</div>
                    <div className='mt-2 w-25'>
                      <LinearProgress variant='determinate' color='secondary' value={progress} />
                    </div>
                    <CancelIcon className='pointer' onClick={(e) => onDocDelete()} />
                  </div>
                </div>
              ) : (
                <div
                  className={uploadError ? 'error' : ''}
                  {...getRootProps({ style })}
                  onClick={open}
                >
                  <input {...getInputProps()} />
                  <div className='d-flex align-items-center'>
                    <Attachment />
                    <span className='ms-1'>
                      Drag and drop or <span className='text-highlight pointer'>Browse</span>
                    </span>
                  </div>
                </div>
              )}
              {imageError && (
                <div className='input-error mt-4'>
                  <Error className='me-1' fontSize='small' />
                  Sorry! Unable to add document while menu image(s) exists. Please remove the images
                  and try again.
                </div>
              )}
              {uploadError && (
                <div className='mt-1'>
                  Document Upload failed.{' '}
                  <span onClick={(e) => onDocDelete()} className='link-text text-red'>
                    Try Again
                  </span>
                </div>
              )}
            </div>
          )}
          {documentType === 'WEB_URL' && (
            <div className='col-md-12'>
              <TextField
                id='document-url'
                placeholder={webUrlPlaceholder || 'Please paste the document URL here.'}
                value={item.value.url}
                className='col-md-12'
                onChange={(e) => onChange(e)}
                error={getErrorMessage() ? true : false}
                helperText={getErrorMessage()}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomUpload;
